import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { QueriesTypeSchema } from '../../telematics/types';
import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import type { TacticalPlanResponseType } from './TacticalPlanApi';
import { TacticalPlanResponseSchema } from './TacticalPlanApi';
import { CountSchema } from '../../Vehicles/types';

// https://lm.nemo.indigo/docs/#req_73648c6613c7493a882e6f2e5daa6a6f

export const TacticalPlanRuleRequestSchema = z.object({
    tactical_plan_id: z.number().nullable(),
    name: z.string().catch(''),

    priority: z.number().catch(0),
    auto_confirm: z
        .boolean()
        .catch(false)
        .describe('auto confirm for all deliveries (user will not have to confirm each delivery)'),
    details: z.object({
        errors: z
            .array(z.string())
            .catch([])
            .describe(
                'array of errors as keys to translate or something else ex:   "zone-editor-conflict-intersection": "Strefa {zone} nakłada się na inne."' +
                    '  "zone-editor-conflict-not-a-polygon": "Strefa {zone} jest niepoprawna."' +
                    '  "zone-editor-conflict-self-intersection": "Strefa {zone} zawiera samoprzecięcia.' +
                    '  "zone-editor-conflict-zone-too-big": "Strefa {zone} jest zbyt rozległa.'
            ),
        filter: z
            .any()
            .transform(val => (Array.isArray(val) && val.length == 0 ? {} : val))
            .transform(val => (val === null ? {} : val))
            .catch({}),
        // z
        //   .union([
        //       z.record(z.literal('$and'), z.array(QueriesTypeSchema)),
        //       z.record(z.literal('$or'), z.array(QueriesTypeSchema)),
        //       QueriesTypeSchema,
        //   ])
        //   .nullable(),
        // .catch(null)
        // .transform(val => (val === null ? {} : val)),
        //.catch('zero'),
        // ).transform((val) => val===null || (Array.isArray(val) && val.length===0) ? {} : val),
        vehicle_id: z.number().nullable().catch(null),
        action: z.union([
            z
                .object({
                    type: z.literal('vehicle'),
                    vehicle_id: z.number().nullable().catch(null),
                })
                .describe('DEPRECATED'),
            z.object({
                type: z.literal('map'),
                map_id: z.number().nullable().catch(null),
            }),
            z.object({
                type: z.literal('unset'),
            }),
        ]),
    }),
});
export type TacticalPlanRuleRequestType = z.infer<typeof TacticalPlanRuleRequestSchema>;

export const TacticalPlanRuleResponseSchema = TacticalPlanRuleRequestSchema.merge(
    z.object({
        id: z.number().nullable(),
        created: z.string(),
        updated: z.string(),
        revision: z.string().catch(''),
        tactical_plan_rule_type_id: z.number().optional(),
    })
);
export type TacticalPlanRuleResponseType = z.infer<typeof TacticalPlanRuleResponseSchema>;

export const MapsWithTacticalPlanDetailsResponseSchema = z.object({
    name: z.string(), // rule name
    id: z.number().describe('rule id'),
    details: z.object({
        action: z.object({
            type: z.literal('map'),
            map_id: z.number().nullable(),
        }),
    }),
    tactical_plan: z.object({
        name: z.string(),
    }),
});
export type MapsWithTacticalPlanDetailsResponseType = z.infer<
    typeof MapsWithTacticalPlanDetailsResponseSchema
>;

export class TacticalPlanRuleApi {
    public static async getAll(api: Api): Promise<TacticalPlanRuleResponseType[]> {
        return api.getData<undefined, TacticalPlanRuleResponseType[]>(
            'get',
            `/tactical_plan_rules`,
            undefined,
            z.undefined(),
            z.array(TacticalPlanRuleResponseSchema)
        );
    }

    public static async getOne(api: Api, id: number): Promise<TacticalPlanRuleResponseType> {
        return api.getData<undefined, TacticalPlanRuleResponseType>(
            'get',
            `/tactical_plan_rule/${id}`,
            undefined,
            z.undefined(),
            TacticalPlanRuleResponseSchema
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<TacticalPlanRuleResponseType[]> {
        return api.getData<undefined, TacticalPlanRuleResponseType[]>(
            'get',
            `/tactical_plan_rule/` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(TacticalPlanRuleResponseSchema)
        );
    }

    public static async getByTacticalPlanId(
        api: Api,
        tacticalPlanId: number
    ): Promise<TacticalPlanRuleResponseType[]> {
        let f = new ApiFilterBuilder(100);
        f.addFilterByNumber('tactical_plan_id', tacticalPlanId);
        return api.getData<undefined, TacticalPlanRuleResponseType[]>(
            'get',
            `/tactical_plan_rule/` + f.getUri(),
            undefined,
            z.undefined(),
            z.array(TacticalPlanRuleResponseSchema)
        );
    }

    public static async createOne(
        api: Api,
        req: TacticalPlanRuleRequestType
    ): Promise<TacticalPlanRuleResponseType> {
        return api.getData<TacticalPlanRuleRequestType, TacticalPlanRuleResponseType>(
            'post',
            `/tactical_plan_rule/`,
            req,
            TacticalPlanRuleRequestSchema,
            TacticalPlanRuleResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        id: number,
        req: TacticalPlanRuleRequestType
    ): Promise<TacticalPlanRuleResponseType> {
        return api.getData<TacticalPlanRuleRequestType, TacticalPlanRuleResponseType>(
            'put',
            `/tactical_plan_rule/${id}`,
            req,
            TacticalPlanRuleRequestSchema,
            TacticalPlanRuleResponseSchema
        );
    }

    public static async removeOne(api: Api, id: number): Promise<void> {
        return api.delete(`/tactical_plan_rule/${id}`);
    }

    public static async patch(
        api: Api,
        id: number,
        path: string,
        value: any
    ): Promise<TacticalPlanRuleResponseType> {
        return api.patch<TacticalPlanRuleResponseType>(
            `/tactical_plan_rule/${id}`,
            'replace',
            path,
            value,
            TacticalPlanRuleResponseSchema
        );
    }

    public static async getMapsWithTacticalPlanDetails(
        api: Api,
        excludeRuleId: number | null
    ): Promise<MapsWithTacticalPlanDetailsResponseType[]> {
        let f = new ApiFilterBuilder(null);
        let request = {
            'details.action.type': 'map',
            id: { $ne: excludeRuleId },
        };
        if (excludeRuleId === null) {
            // @ts-ignore -- id
            request = {
                'details.action.type': 'map',
            };
        }
        f.setRelations(['tactical_plan']);
        f.setProjectionFields([
            'id',
            'tactical_plan.name',
            'tactical_plan_rule.name',
            'tactical_plan_rule.details',
        ]);
        return api.getData<any, MapsWithTacticalPlanDetailsResponseType[]>(
            'post',
            '/tactical_plan_rules/query' + f.getUri(),
            request,
            z.any(),
            z.array(MapsWithTacticalPlanDetailsResponseSchema)
        );
    }

    public static async deepcopy(
        api: Api,
        tacticalPlanRuleId: number
    ): Promise<TacticalPlanRuleResponseType> {
        return api.getData<Object, TacticalPlanRuleResponseType>(
            'post',
            `/tactical_plan_rule/${tacticalPlanRuleId}/deepcopy`,
            {},
            z.object({}),
            TacticalPlanRuleResponseSchema
        );
    }

    public static async getCountByActionType(
        api: Api,
        tacticalPlanId: number,
        actionType: string
    ): Promise<{ count: number }> {
        // POST https://lastmile-test.automapa.pl/api/tactical_plan_rules/count/query?sort%5Bby%5D=id&sort%5Bdir%5D=asc&relations%5B0%5D=tactical_plan
        let f = new ApiFilterBuilder(null);
        f.setRelations(['tactical_plan']);
        f.setProjectionFields([
            'id',
            'tactical_plan.name',
            'tactical_plan_rule.name',
            'tactical_plan_rule.details',
        ]);
        let request = {
            'details.action.type': actionType,
            tactical_plan_id: tacticalPlanId,
        };
        return api.getData<any, { count: number }>(
            'post',
            '/tactical_plan_rules/count/query' + f.getUri(),
            request,
            z.any(),
            CountSchema
        );
    }

    public static async getCountMaps(api: Api, tacticalPlanId: number): Promise<{ count: number }> {
        return this.getCountByActionType(api, tacticalPlanId, 'map');
    }

    // public static async getCountVehicles(
    //     api: Api,
    //     tacticalPlanId: number
    // ): Promise<{ count: number }> {
    //     return api.getData<any, { count: number }>(
    //         'post',
    //         `/tactical_plan/${tacticalPlanId}/count_vehicles`,
    //         {},
    //         z.any(),
    //         CountSchema
    //     );
    //     // return this.getCountByActionType(api, tacticalPlanId, 'vehicle');
    // }
}
